<modal-dialog #editApiKeyModal [dialogTemplate]="editApiKeyDialogTemplate"></modal-dialog>
<ng-template #editApiKeyDialogTemplate>
  <div class="light-content-panel light-control-host" (keydown.escape)="close()">
    <div class="modal-header">
      <h2 class="panel-title margin-left">Edit API Key</h2>
      <button type="button" class="modal-close-btn" (click)="close()">
        <i class="fa-utility fa-semibold fa-xmark"></i>
      </button>
    </div>
    <form [formGroup]="apiKeyForm" (ngSubmit)="onSave()" (keydown.enter)="$event.preventDefault()">
      <div class="modal-body">
        <!-- API Key Information Section -->
        <div class="form-section">
          <div class="role-information-body">
            <div class="form-row">
              <strong>API Key Name <span class="required">*</span></strong>
              <input
                type="text"
                formControlName="name"
                class="arpio-input full-width"
                placeholder="Enter API key name"
              />
              @if (nameErrors) {
                <div class="input-error-text">
                  {{ nameErrors }}
                </div>
              }
            </div>

            <div class="form-row margin-top">
              <label class="form-label flex items-center">
                <input type="checkbox" formControlName="enabled" class="arpio-checkbox" style="margin-bottom: -4px" />
                <span class="margin-left">API Key Enabled</span>
              </label>
              <div class="input-help-text text-xs">
                Disabled API keys cannot be used to access the Arpio API. Toggle this to enable or disable the key.
              </div>
            </div>
          </div>
        </div>
        <hr />

        <!-- Role Assignment Section -->
        <div class="form-section">
          <h3>Assign Roles</h3>
          <div class="table-container">
            <div class="flex-row items-center gap-4 margin-bottom">
              <h5>Select Roles for this API Key</h5>
              <div class="filter-container">
                @if (isModalOpen()) {
                  <input
                    pInputText
                    type="text"
                    #rolesFilter
                    (input)="filterRoles($event)"
                    placeholder="Filter roles..."
                    class="arpio-input filter-input"
                  />
                }
              </div>
            </div>
            @if (isModalOpen()) {
              <p-table
                #rolesTable
                [value]="availableRoles"
                [globalFilterFields]="['name', 'accessLevel']"
                dataKey="id"
                [scrollable]="true"
                scrollHeight="300px"
                [tableStyle]="{ 'min-width': '35rem' }"
                [styleClass]="'p-datatable-sm'"
                [loading]="loadingRoles"
                [customSort]="true"
                (sortFunction)="customSort($event)"
              >
                <ng-template pTemplate="header">
                  <tr>
                    <th scope="col" class="checkbox-column">
                      <input
                        type="checkbox"
                        class="arpio-checkbox"
                        [checked]="areAllRolesSelected()"
                        [indeterminate]="areSomeRolesSelected()"
                        (change)="toggleAllRoles($event)"
                      />
                    </th>
                    <th scope="col" pSortableColumn="name" class="role-name-column">
                      Role Name <p-sortIcon field="name"></p-sortIcon>
                    </th>
                    <th scope="col" pSortableColumn="accessLevel">
                      Access Level(s) <p-sortIcon field="accessLevel"></p-sortIcon>
                    </th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-role>
                  <tr [ngClass]="{ 'default-role-row': isDefaultAccountRole(role) }">
                    <td class="checkbox-column">
                      <input
                        type="checkbox"
                        class="arpio-checkbox"
                        [checked]="isRoleSelected(role)"
                        (change)="toggleRole(role, $event)"
                      />
                    </td>
                    <td class="role-name-column">
                      <div>
                        {{ role.name }}
                      </div>
                    </td>
                    <td>
                      @if (role.accessLevels && role.accessLevels.length > 0) {
                        <div class="access-level-badges">
                          @for (accessLevelInfo of role.accessLevels; track accessLevelInfo) {
                            <p-tag
                              [severity]="
                                accessLevelInfo.accessLevel.includes('admin')
                                  ? 'danger'
                                  : accessLevelInfo.accessLevel.includes('read')
                                    ? 'warning'
                                    : 'info'
                              "
                              class="access-level-badge fit-width"
                              [pTooltip]="getAccessLevelTooltip(accessLevelInfo)"
                              tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                              tooltipPosition="top"
                              [autoHide]="false"
                              [hideDelay]="200"
                              [escape]="false"
                              tooltipEvent="hover"
                              value="{{ accessLevelService.getAccessLevelDisplayName(accessLevelInfo.accessLevel) }}"
                            >
                            </p-tag>
                          }
                        </div>
                      } @else {
                        <div>{{ accessLevelService.getAccessLevelDisplayName(role.accessLevel) }}</div>
                      }
                    </td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                  <tr>
                    <td colspan="3" class="empty-state-cell text-center">
                      <div class="empty-state-content">
                        @if (
                          rolesTable.filteredValue && rolesTable.filteredValue.length === 0 && availableRoles.length > 0
                        ) {
                          <p class="empty-state-message">No roles match your filter</p>
                          <p class="empty-state-submessage">
                            Try adjusting your filter criteria or clear the filter to see all roles.
                          </p>
                        }
                        @if (!rolesTable.filteredValue || availableRoles.length === 0) {
                          <p class="empty-state-message">No roles available</p>
                          <p class="empty-state-submessage">
                            Create roles in Role Management to assign them to API keys.
                          </p>
                        }
                      </div>
                    </td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="summary">
                  @if (!loadingRoles) {
                    <div class="flex align-items-center justify-content-between">
                      <span
                        >Displaying {{ rolesTable.filteredValue?.length ?? rolesTable.value?.length }} of
                        {{ rolesTable.value?.length }} roles</span
                      >
                    </div>
                  }
                </ng-template>
              </p-table>
            }
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button type="button" class="arpio-secondary-button" (click)="close()">CANCEL</button>
        <button type="submit" class="arpio-button" [disabled]="!isFormValid || saving">
          @if (saving) {
            <i class="fa-solid fa-spinner fa-spin"></i>
          }
          {{ getSaveButtonText() }}
        </button>
      </div>
    </form>
  </div>
</ng-template>
